import React, { useState, useEffect } from 'react';
import { 
  Calculator,
  CheckCircle,
  Shield,
  ToggleLeft,
  ToggleRight,
  Target
} from 'lucide-react';

const CalculationSettings = ({ 
  settings,
  handleSettingChange,
  currentCompany
}) => {
  
  const [previewCalculation, setPreviewCalculation] = useState(null);

  // Generate preview calculation
  const generatePreviewCalculation = (currentSettings) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const dailyRate = 25; // Example rate
    
    // Same day rental
    const sameDayInclusive = currentSettings.rentalCalculationMethod === 'inclusive' ? 1 : 1;
    const sameDayExclusive = 1;
    
    // Overnight rental (today to tomorrow)
    const overnightInclusive = currentSettings.rentalCalculationMethod === 'inclusive' ? 2 : 1;
    const overnightExclusive = 1;
    
    setPreviewCalculation({
      dailyRate,
      scenarios: [
        {
          name: 'Same Day Return',
          pickup: 'Today 10:00 AM',
          dropoff: 'Today 6:00 PM',
          daysInclusive: sameDayInclusive,
          daysExclusive: sameDayExclusive,
          amountInclusive: sameDayInclusive * dailyRate,
          amountExclusive: sameDayExclusive * dailyRate
        },
        {
          name: 'Overnight Rental',
          pickup: 'Today 10:00 AM',
          dropoff: 'Tomorrow 10:00 AM',
          daysInclusive: overnightInclusive,
          daysExclusive: overnightExclusive,
          amountInclusive: overnightInclusive * dailyRate,
          amountExclusive: overnightExclusive * dailyRate
        }
      ]
    });
  };

  // Generate initial preview
  useEffect(() => {
    generatePreviewCalculation(settings);
  }, [settings.rentalCalculationMethod, settings.minimumRentalDays, settings.allowSameDayRental]);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
        <h3 className="text-xl font-semibold text-white flex items-center gap-3">
          <Calculator className="w-6 h-6 text-orange-500" />
          Rental Calculation Method
          <div className="ml-auto flex items-center text-orange-400 text-xs">
            <Target size={12} className="mr-1" />
            <span>Core Feature</span>
          </div>
        </h3>
        <p className="text-gray-400 text-sm mt-1">
          Configure how rental days are calculated for pricing
        </p>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Calculation Method Toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Day Calculation Method
          </label>
          <div className="space-y-3">
            <label className="flex items-center p-3 bg-gray-700/50 rounded-lg border border-gray-600 cursor-pointer hover:bg-gray-700/70 transition-colors">
              <input
                type="radio"
                name="calculationMethod"
                value="inclusive"
                checked={settings.rentalCalculationMethod === 'inclusive'}
                onChange={(e) => handleSettingChange('rentalCalculationMethod', e.target.value)}
                className="mr-3 text-orange-500"
              />
              <div className="flex-1">
                <div className="text-white font-medium">Inclusive Days (Recommended)</div>
                <div className="text-gray-400 text-sm">
                  Each calendar day counts as 1 day. Today + Tomorrow = 2 days
                </div>
              </div>
              {settings.rentalCalculationMethod === 'inclusive' && (
                <CheckCircle size={20} className="text-green-400" />
              )}
            </label>
            
            <label className="flex items-center p-3 bg-gray-700/50 rounded-lg border border-gray-600 cursor-pointer hover:bg-gray-700/70 transition-colors">
              <input
                type="radio"
                name="calculationMethod"
                value="exclusive"
                checked={settings.rentalCalculationMethod === 'exclusive'}
                onChange={(e) => handleSettingChange('rentalCalculationMethod', e.target.value)}
                className="mr-3 text-orange-500"
              />
              <div className="flex-1">
                <div className="text-white font-medium">Duration-Based</div>
                <div className="text-gray-400 text-sm">
                  Based on actual time span. Same day return = 1 day
                </div>
              </div>
              {settings.rentalCalculationMethod === 'exclusive' && (
                <CheckCircle size={20} className="text-green-400" />
              )}
            </label>
          </div>
        </div>

        {/* Preview Calculation Examples */}
        {previewCalculation && (
          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <Calculator size={16} className="text-blue-400" />
              Calculation Preview (Daily Rate: ${previewCalculation.dailyRate})
            </h4>
            <div className="space-y-3">
              {previewCalculation.scenarios.map((scenario, index) => (
                <div key={index} className="bg-gray-800/50 rounded-lg p-3 border border-gray-600">
                  <div className="text-white font-medium mb-2">{scenario.name}</div>
                  <div className="text-sm text-gray-400 mb-2">
                    {scenario.pickup} → {scenario.dropoff}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className={`p-2 rounded ${
                      settings.rentalCalculationMethod === 'inclusive' 
                        ? 'bg-green-900/30 border border-green-700' 
                        : 'bg-gray-700/30 border border-gray-600'
                    }`}>
                      <div className="text-gray-400">Inclusive Method:</div>
                      <div className={`font-medium ${
                        settings.rentalCalculationMethod === 'inclusive' 
                          ? 'text-green-300' 
                          : 'text-gray-300'
                      }`}>
                        {scenario.daysInclusive} days = ${scenario.amountInclusive}
                      </div>
                    </div>
                    <div className={`p-2 rounded ${
                      settings.rentalCalculationMethod === 'exclusive' 
                        ? 'bg-green-900/30 border border-green-700' 
                        : 'bg-gray-700/30 border border-gray-600'
                    }`}>
                      <div className="text-gray-400">Duration-Based:</div>
                      <div className={`font-medium ${
                        settings.rentalCalculationMethod === 'exclusive' 
                          ? 'text-green-300' 
                          : 'text-gray-300'
                      }`}>
                        {scenario.daysExclusive} days = ${scenario.amountExclusive}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-blue-300 bg-blue-900/20 p-2 rounded border border-blue-700">
              💡 The highlighted calculation shows what customers will be charged based on your selected method
            </div>
          </div>
        )}

        {/* Other Calculation Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Minimum Rental Days
            </label>
            <input
              type="number"
              min="1"
              max="7"
              value={settings.minimumRentalDays}
              onChange={(e) => handleSettingChange('minimumRentalDays', parseInt(e.target.value))}
              className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum number of days required for a rental
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Auto Calculate Amount
            </label>
            <button
              onClick={() => handleSettingChange('autoCalculateAmount', !settings.autoCalculateAmount)}
              className={`flex items-center w-full p-2 rounded-lg border transition-colors ${
                settings.autoCalculateAmount 
                  ? 'bg-green-900/30 border-green-700 text-green-300'
                  : 'bg-gray-700 border-gray-600 text-gray-300'
              }`}
            >
              {settings.autoCalculateAmount ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              <span className="ml-2">
                {settings.autoCalculateAmount ? 'Enabled' : 'Disabled'}
              </span>
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Automatically calculate rental amount based on days and bike rate
            </p>
          </div>
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.allowSameDayRental}
              onChange={(e) => handleSettingChange('allowSameDayRental', e.target.checked)}
              className="mr-3 text-orange-500"
            />
            <span className="text-gray-300">Allow same-day pickup and return</span>
          </label>
          <p className="text-xs text-gray-500 ml-6 mt-1">
            Enable customers to rent and return bikes on the same day
          </p>
        </div>

        {/* Validation Rules */}
        <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Shield size={16} className="text-orange-400" />
            Advanced Validation Rules
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Customer Name Minimum Length
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.validationRules?.customerNameMinLength || 2}
                onChange={(e) => handleSettingChange('validationRules', {
                  ...settings.validationRules,
                  customerNameMinLength: parseInt(e.target.value)
                })}
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum number of characters required for customer name
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Validation Level
              </label>
              <select
                value={settings.validationRules?.emailValidation || 'strict'}
                onChange={(e) => handleSettingChange('validationRules', {
                  ...settings.validationRules,
                  emailValidation: e.target.value
                })}
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="strict">Strict (Full validation)</option>
                <option value="basic">Basic (Simple check)</option>
                <option value="none">None (No validation)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Level of email format validation
              </p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.validationRules?.requirePhoneOrEmail || false}
                  onChange={(e) => handleSettingChange('validationRules', {
                    ...settings.validationRules,
                    requirePhoneOrEmail: e.target.checked
                  })}
                  className="mr-3 text-orange-500"
                />
                <span className="text-gray-300">Require either phone or email (at least one contact method)</span>
              </label>
              <p className="text-xs text-gray-500 ml-6 mt-1">
                Customer must provide at least one contact method
              </p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.validationRules?.allowPastDates || false}
                  onChange={(e) => handleSettingChange('validationRules', {
                    ...settings.validationRules,
                    allowPastDates: e.target.checked
                  })}
                  className="mr-3 text-orange-500"
                />
                <span className="text-gray-300">Allow past dates for pickup/dropoff</span>
              </label>
              <p className="text-xs text-gray-500 ml-6 mt-1">
                Enable creating reservations with past dates (for record keeping)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Maximum Advance Booking (Days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={settings.validationRules?.maxAdvanceBookingDays || 365}
                onChange={(e) => handleSettingChange('validationRules', {
                  ...settings.validationRules,
                  maxAdvanceBookingDays: parseInt(e.target.value)
                })}
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                How far in advance customers can book (1-365 days)
              </p>
            </div>
          </div>
        </div>

        {/* Calculation Summary */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <CheckCircle size={16} className="text-orange-400" />
            Current Configuration Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div>
              <span className="text-gray-400">Method:</span>
              <span className="text-white ml-2 font-medium capitalize">
                {settings.rentalCalculationMethod}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Min Days:</span>
              <span className="text-white ml-2 font-medium">
                {settings.minimumRentalDays}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Auto Calc:</span>
              <span className={`ml-2 font-medium ${settings.autoCalculateAmount ? 'text-green-400' : 'text-red-400'}`}>
                {settings.autoCalculateAmount ? 'Yes' : 'No'}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Same Day:</span>
              <span className={`ml-2 font-medium ${settings.allowSameDayRental ? 'text-green-400' : 'text-red-400'}`}>
                {settings.allowSameDayRental ? 'Allowed' : 'Not Allowed'}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Email:</span>
              <span className="text-white ml-2 font-medium capitalize">
                {settings.validationRules?.emailValidation || 'Strict'}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Contact Req:</span>
              <span className={`ml-2 font-medium ${settings.validationRules?.requirePhoneOrEmail ? 'text-orange-400' : 'text-gray-400'}`}>
                {settings.validationRules?.requirePhoneOrEmail ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculationSettings;